// Editor de regra de desembolso (espec. 8.3). Offcanvas prop-driven no padrão do
// `DisbursementCoverageOffcanvas`, com o que a espec. 8.5 pede por cima: `role="dialog"`,
// foco que entra ao abrir e volta ao fechar, e fechamento por Escape.
//
// Este arquivo é a máquina de estado: alvo, atalho, formulário, linhas, validação e
// salvar. A apresentação está em três irmãos — `RuleTargetPicker` (passo 1, mais o
// contrato do alvo), `RuleShortcutFields` e `RuleProfileTable` (passo 2).
//
// Passo 3 (Task 17): a pré-visualização é LOCAL (`RulePreviewChart` + `previewTargetCurve`)
// e o salvar fala com a rota por aqui — a menos que o chamador passe `onSave`, caso em
// que o editor só delega (contrato da Task 16, para quem já grava por conta).
import { useEffect, useMemo, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";

import DescribeRuleWithAi from "./ai/DescribeRuleWithAi";
import { confirmRuleWithAi, newIdempotencyKey } from "./ai/disbursementAiApi";
import {
  fetchDisbursementTargets,
  saveDisbursementRule,
} from "./disbursementApi";
import { disbursementErrorDetail } from "./errorMessages";
import {
  MAX_LINES,
  ProfileError,
  describeProfile,
  validateProfile,
} from "./profile";
import { ORIGIN_NAMES } from "./OriginSelector";
import RuleImpactSection from "./RuleImpactSection";
import RulePreviewChart from "./RulePreviewChart";
import RuleProfileTable, {
  PROFILE_ERROR_ID,
  seedLines,
  withLineId,
} from "./RuleProfileTable";
import RuleShortcutFields, { buildShortcutProfile } from "./RuleShortcutFields";
import useRuleImpact, { IMPACT_DEBOUNCE_MS, requiresImpactPreview } from "./useRuleImpact";
import RuleTargetPicker, {
  TargetValue,
  normalizeTarget,
  ruleTargetLevel,
  ruleTargetOf,
  targetQueryLevel,
} from "./RuleTargetPicker";

// O contrato do alvo mora no `RuleTargetPicker`, mas a Task 17 o consome pelo editor.
export { normalizeTarget, ruleTargetLevel, ruleTargetOf, targetQueryLevel };

const SEARCH_DEBOUNCE_MS = 350;
const SAVE_ERROR_TEXT = "Não foi possível salvar a regra. Tente novamente.";
const SAVE_ERROR_PREFIX = "Não foi possível salvar a regra.";
// A obra mudou entre a prévia de impacto e a confirmação: nada foi gravado e a prévia é
// recalculada sozinha, para o cliente confirmar sobre o que de fato vai acontecer.
const STALE_PREVIEW_TEXT =
  "A obra mudou depois da prévia de impacto. A prévia foi recalculada; confira o novo impacto antes de reprogramar.";

// `conflictingGroup` chega como objeto `{ groupId, name }` (contrato da Task 13) — a
// string crua é só a forma tolerada. Interpolar o objeto direto imprimiria
// "[object Object]" na cara do cliente.
const conflictingGroupName = (group) =>
  typeof group === "string" ? group : group?.name;

const overlapText = (group) => {
  const name = conflictingGroupName(group);
  return name
    ? `Este alvo já está coberto pelo grupo "${name}".`
    : "Este alvo já está coberto por outro grupo.";
};

const saveErrorText = (result) => {
  const errorCode = result?.errorCode;
  if (errorCode === "impact_preview_stale") return STALE_PREVIEW_TEXT;
  if (errorCode === "group_overlap") {
    return `${SAVE_ERROR_PREFIX} ${overlapText(result?.conflictingGroup)}`;
  }
  // Vocabulário do `rulePlanner`/`rulesStore`: `invalid_target` e `rule_not_found`.
  // Não existe `target_invalid`/`target_not_found` no back-end — mapear aqueles
  // deixaria o cliente com a frase genérica justamente nos erros de alvo.
  const detail = disbursementErrorDetail(errorCode);
  return detail ? `${SAVE_ERROR_PREFIX} ${detail}` : SAVE_ERROR_TEXT;
};

const DEFAULT_FORM = Object.freeze({
  unit: "months",
  installments: 1,
  startOffset: 0,
  downPaymentBps: 3000,
  downPaymentOffset: -1,
});

const profileErrorText = (error) =>
  error instanceof ProfileError
    ? error.message
    : "Forma de desembolso inválida";

const RuleEditorOffcanvas = ({
  open,
  onClose,
  project = null,
  projectId = null,
  target = null,
  initialTarget = null,
  profile = null,
  initialRule = null,
  initialSource = "manual",
  rulesByRuleId = null,
  readOnly = false,
  onChangeTarget,
  onChangeProfile,
  onSave,
  onSaved,
  // A origem do desembolso da obra (`state.origin`): nomeia a série de comparação da
  // pré-visualização e a origem vigente no cabeçalho do alvo.
  origin = null,
  impactDebounceMs = IMPACT_DEBOUNCE_MS,
}) => {
  const workId = projectId ?? project?._id ?? null;
  const seededTarget = useMemo(
    () => normalizeTarget(target ?? initialTarget),
    [target, initialTarget],
  );
  const seededProfile = profile ?? initialRule?.profile ?? null;

  const [targetType, setTargetType] = useState(
    (target ?? initialTarget)?.kind === "node" ? "node" : "erp_group",
  );
  const [nodeLevel, setNodeLevel] = useState("substep");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetsError, setTargetsError] = useState(false);
  const [chosenTarget, setChosenTarget] = useState(seededTarget);
  // Semear a partir de uma regra existente cai em "Personalizado": salvar de novo sem
  // tocar em nada tem que preservar exatamente o perfil gravado, não regenerá-lo.
  const [shortcut, setShortcut] = useState(seededProfile ? "custom" : "equal");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [customLines, setCustomLines] = useState(() =>
    seedLines(seededProfile),
  );
  const [description, setDescription] = useState(
    initialRule?.description ?? "",
  );
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const confirmationRequest = useRef(null);
  const confirmedResult = useRef(null);
  // Origem do perfil gravado (espec. 10.3): a IA preenche com `"ai_assistant"`, e
  // QUALQUER edição manual depois — atalho, linha, campo do atalho — volta para
  // `"manual"`. Sem isso o rótulo de origem na lista mentiria sobre um perfil que o
  // cliente reescreveu depois da proposta.
  const [ruleSource, setRuleSource] = useState(initialSource);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Série do previsto buscada quando o alvo chega SEM ela (linha da lista de regras
  // não carrega `plannedSeries`): guardada junto com a chave do alvo, pra nunca
  // vazar o previsto de um alvo para outro, e com o `status` da busca
  // (`loading`/`ready`/`failed`) — "ainda não chegou" e "não tem" são coisas
  // diferentes na tela.
  const [hydrated, setHydrated] = useState(null);
  const [overwriteWarning, setOverwriteWarning] = useState(false);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  // A identidade do alvo semeado é a CHAVE, não o objeto: um pai que remonta a prop a
  // cada render (ou recarrega o mesmo alvo da rota) não pode resetar o editor.
  const seedKey = JSON.stringify([
    workId,
    seededTarget?.key ?? null,
    seededProfile,
    initialSource,
    open,
  ]);
  const lastSeededKey = useRef(seedKey);
  const saveScope = useRef(0);
  const renderedSeed = useRef(seedKey);
  if (renderedSeed.current !== seedKey) {
    renderedSeed.current = seedKey;
    saveScope.current += 1;
  }
  useEffect(
    () => () => {
      saveScope.current += 1;
    },
    [],
  );
  // Chave cuja busca de previsto já foi disparada. É um ref, não estado: se o estado
  // `hydrated` entrasse nas dependências do efeito, marcar "carregando" o re-rodaria
  // e a limpeza cancelaria a própria busca que acabou de sair.
  const hydrationRequest = useRef(null);

  const queryLevel = targetType === "node" ? nodeLevel : targetType;

  // Alvo controlado pelo chamador: quando a chave da prop muda, o editor segue.
  useEffect(() => {
    const key = seedKey;
    if (lastSeededKey.current === key) return;
    lastSeededKey.current = key;
    setChosenTarget(seededTarget);
    // Alvo semeado NOVO leva o resto do formulário com ele: editar uma regra pela
    // lista (`onEditRule`) cai em "Personalizado" com o perfil GRAVADO, e abrir para
    // um alvo sem regra volta ao padrão — nunca sobra a forma do alvo anterior.
    setShortcut(seededProfile ? "custom" : "equal");
    setCustomLines(seedLines(seededProfile));
    setDescription(initialRule?.description ?? "");
    setDescriptionDirty(false);
    setAiDraft(null);
    setSaving(false);
    confirmationRequest.current = null;
    confirmedResult.current = null;
    setOverwriteWarning(false);
    setSaveError(null);
    setHydrated(null);
    setRuleSource(initialSource);
    hydrationRequest.current = null;
  }, [seedKey, seededTarget, seededProfile, initialRule, initialSource]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Foco entra no painel ao abrir e volta pro gatilho ao fechar (espec. 8.5).
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const previous = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (
        previous &&
        typeof previous.focus === "function" &&
        document.body.contains(previous)
      ) {
        previous.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [open, search]);

  useEffect(() => {
    if (!open || chosenTarget || !workId) return undefined;
    // `cancelled` é a guarda contra resposta desatualizada: trocar o filtro enquanto
    // uma busca voa não pode deixar a resposta antiga sobrepor a mais recente.
    let cancelled = false;
    (async () => {
      setLoadingTargets(true);
      setTargetsError(false);
      const payload = await fetchDisbursementTargets(workId, {
        level: queryLevel,
        search: debouncedSearch,
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        setCandidates([]);
        setTargetsError(true);
        setLoadingTargets(false);
        return;
      }
      setCandidates(payload.items.map(normalizeTarget));
      setLoadingTargets(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, chosenTarget, workId, queryLevel, debouncedSearch]);

  // Alvo sem previsto na mão: busca a série do próprio alvo antes de pré-visualizar.
  // Sem isso a pré-visualização de uma regra existente sairia zerada — e um zero na
  // tela o cliente lê como resposta do motor, não como dado que faltou.
  useEffect(() => {
    if (!open || !workId || !chosenTarget) return undefined;
    if ((chosenTarget.plannedSeries?.length ?? 0) > 0) return undefined;
    const requestKey = JSON.stringify([workId, chosenTarget.key]);
    if (hydrationRequest.current === requestKey) return undefined;
    // `?level=` é OBRIGATÓRIO na rota de alvos (400 `invalid_target_level` sem ele) e
    // não aceita o nível de armazenamento `resource_group`: o nível de consulta é
    // derivado AQUI, na hora do fetch. Sem nível derivável não há consulta possível —
    // o alvo fica com a série que ele já tem (vazia) e o gráfico dá o veredito
    // honesto, em vez de gastar um 400 que morreria calado.
    const queryLevelForTarget = targetQueryLevel(chosenTarget);
    if (!queryLevelForTarget) {
      setHydrated({
        key: chosenTarget.key,
        series: chosenTarget.plannedSeries ?? [],
        status: "ready",
      });
      return undefined;
    }
    hydrationRequest.current = requestKey;
    let cancelled = false;
    setHydrated({ key: chosenTarget.key, series: [], status: "loading" });
    (async () => {
      const payload = await fetchDisbursementTargets(workId, {
        level: queryLevelForTarget,
        search:
          chosenTarget.kind === "node"
            ? chosenTarget.key.slice("node:".length, "node:".length + 80)
            : String(chosenTarget.label ?? "").slice(0, 80),
      });
      if (cancelled) return;
      if (!payload || !Array.isArray(payload.items)) {
        // Envelope de erro (ou `null` de rede) é FALHA, não "alvo sem previsto".
        setHydrated({ key: chosenTarget.key, series: [], status: "failed" });
        return;
      }
      const match = payload.items
        .map(normalizeTarget)
        .find((candidate) => candidate.key === chosenTarget.key);
      setHydrated({
        workId,
        key: chosenTarget.key,
        series: match?.plannedSeries ?? [],
        plannedCents: match?.plannedCents ?? null,
        status: "ready",
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workId, chosenTarget]);

  const previewSeries = useMemo(() => {
    if ((chosenTarget?.plannedSeries?.length ?? 0) > 0)
      return chosenTarget.plannedSeries;
    return hydrated?.key && hydrated.key === chosenTarget?.key
      ? hydrated.series
      : [];
  }, [chosenTarget, hydrated]);

  // Sem obra não há o que buscar: cai direto no veredito honesto do gráfico em vez
  // de girar "carregando" para sempre.
  const previewHydration = useMemo(() => {
    if (!chosenTarget || !workId) return "ready";
    if ((chosenTarget.plannedSeries?.length ?? 0) > 0) return "ready";
    return hydrated?.key === chosenTarget.key ? hydrated.status : "loading";
  }, [chosenTarget, workId, hydrated]);

  // O perfil visível é SEMPRE a tabela: os atalhos só a preenchem (espec. 8.3). Assim o
  // cliente vê exatamente o que vai ser gravado, e o "Personalizado" é a mesma tabela.
  const visible = useMemo(() => {
    if (shortcut === "custom") return { lines: customLines, error: null };
    try {
      // Chaves determinísticas: `validateProfile` garante (unidade, defasagem) única no
      // gerado, então unidade + defasagem + atalho identifica a linha sem depender da
      // posição. Cada linha vira a forma interna uniforme `{ unit, offset, percentBps }`
      // (profile.js aceita `unit: "months"` com `offset` exatamente como `offsetMonths`),
      // então a tabela e o validador nunca precisam ramificar por forma.
      const lines = buildShortcutProfile(shortcut, form).map((line) => {
        const unit = line.unit ?? "months";
        const offset = line.unit === undefined ? line.offsetMonths : line.offset;
        return { lineId: `${shortcut}:${unit}:${offset}`, unit, offset, percentBps: line.percentBps };
      });
      return { lines, error: null };
    } catch (error) {
      return { lines: [], error: profileErrorText(error) };
    }
  }, [shortcut, form, customLines]);

  // `validateProfile`/`describeProfile` lançam: o resumo só é pedido depois de validar.
  const validation = useMemo(() => {
    if (visible.error)
      return { profile: null, message: visible.error, summary: null };
    try {
      const validated = validateProfile(
        visible.lines.map(({ unit, offset, percentBps }) => ({ unit, offset, percentBps })),
      );
      return {
        profile: validated,
        message: null,
        summary: describeProfile(validated),
      };
    } catch (error) {
      return { profile: null, message: profileErrorText(error), summary: null };
    }
  }, [visible]);

  // Só notifica com perfil VÁLIDO e só com o editor aberto: um offcanvas fechado não
  // tem por que empurrar perfil semeado para a página que o hospeda.
  useEffect(() => {
    if (!open || !validation.profile) return;
    onChangeProfile?.(validation.profile);
  }, [open, validation.profile, onChangeProfile]);

  // Modo apresentação é gate duro: um `readOnly={false}` do chamador não o destrava.
  const isReadOnly = readOnly || isPresentationProject(project);

  // A prévia dos serviços impactados (design D13): pedida com a forma VÁLIDA, só para alvos
  // de insumo, família ou grupo, e nunca em modo apresentação. Fica antes do retorno do
  // editor fechado — um hook não pode depender de o painel estar aberto.
  const needsImpact = requiresImpactPreview(chosenTarget);
  const impact = useRuleImpact({
    workId,
    target: chosenTarget ? ruleTargetOf(chosenTarget) : null,
    profile: validation.profile,
    enabled: open && !isReadOnly && needsImpact,
    debounceMs: impactDebounceMs,
  });

  if (!open) return null;
  const executionComplete = Boolean(confirmedResult.current);
  const titleText = initialRule
    ? "Editar regra de desembolso"
    : "Nova regra de desembolso";
  // Para alvos com prévia, a ação principal só habilita com a prévia CALCULADA (ou com a
  // declaração de que a obra não tem origem utilizável para calcular).
  const impactSettled = !needsImpact || impact.status === "ready" || impact.status === "unavailable";
  const canSave =
    !isReadOnly && !!chosenTarget && !!validation.profile && !saving && impactSettled;
  const impactedCount = impact.answer?.totals?.impactedCount ?? 0;
  const missing = (() => {
    if (isReadOnly || saving || executionComplete || !chosenTarget || canSave) return null;
    if (!validation.profile) return "A forma de desembolso ainda não está completa e válida.";
    if (impact.status === "error") return "Calcule o impacto novamente para reprogramar.";
    return "Aguardando o cálculo do impacto nos serviços.";
  })();
  const originName = origin?.id ? ORIGIN_NAMES[origin.id] : null;

  // Espec. 4.1: escolher um alvo que JÁ tem regra carrega o perfil GRAVADO. Sobrescrever
  // em silêncio com o padrão à vista apagaria o contrato do cliente sem ele ver.
  const chooseTarget = (candidate) => {
    if (executionComplete) return;
    if (aiDraft && candidate?.key !== aiDraft.targetKey) {
      setSaveError("Descarte a proposta de IA antes de escolher outro alvo.");
      return;
    }
    setChosenTarget(candidate);
    setSaveError(null);
    setRuleSource("manual");
    const stored = candidate?.ruleId
      ? rulesByRuleId?.[candidate.ruleId] ?? null
      : null;
    if (stored?.profile) {
      setCustomLines(seedLines(stored.profile));
      setShortcut("custom");
      setDescription(stored.description ?? "");
      setOverwriteWarning(false);
    } else {
      // Alvo com regra cuja forma não está à mão: o aviso é a pista de que salvar
      // aqui substitui o que está gravado.
      setOverwriteWarning(Boolean(candidate?.hasRule || candidate?.ruleId));
    }
    onChangeTarget?.(candidate);
  };

  // Proposta da IA (espec. 10.3, `DescribeRuleWithAi`): preenche alvo E perfil de uma
  // vez. O atalho SEMPRE vira "Personalizado" — sem isso a lógica de atalho (`visible`,
  // acima) regeneraria o perfil semeado a partir do `form` no próximo render, e o
  // perfil que a IA propôs nunca chegaria a aparecer na tela.
  const applyAiProposal = ({
    target: aiTarget,
    profile: aiProfile,
    draftId,
    version,
  }) => {
    if (isReadOnly) return;
    if (executionComplete) return;
    if (
      typeof draftId !== "string" ||
      !draftId ||
      !Number.isSafeInteger(version) ||
      version < 1
    ) {
      setSaveError("A proposta não possui um rascunho válido. Gere novamente.");
      return;
    }
    const normalized = normalizeTarget(aiTarget);
    if (normalized?.key !== chosenTarget?.key) {
      setDescription("");
      setDescriptionDirty(false);
    }
    setAiDraft({ draftId, version, targetKey: normalized.key, workId });
    confirmationRequest.current = null;
    confirmedResult.current = null;
    setChosenTarget(normalized);
    setCustomLines(seedLines(aiProfile));
    setShortcut("custom");
    setRuleSource("ai_assistant");
    setSaveError(null);
    setOverwriteWarning(false);
    hydrationRequest.current = null;
    setHydrated(null);
    onChangeTarget?.(normalized);
  };

  const updateCustomLine = (lineId, patch) => {
    if (executionComplete) return;
    setRuleSource("manual");
    setCustomLines((lines) =>
      lines.map((line) =>
        line.lineId === lineId ? { ...line, ...patch } : line,
      ),
    );
  };

  const addCustomLine = () => {
    if (executionComplete) return;
    setRuleSource("manual");
    setCustomLines((lines) => {
      // Teto do validador (`MAX_LINES`): recusar aqui é melhor que gravar e ouvir 400.
      if (lines.length >= MAX_LINES) return lines;
      const last = lines[lines.length - 1];
      return [
        ...lines,
        withLineId({
          unit: last?.unit ?? "months",
          offset: (Number.isFinite(last?.offset) ? last.offset : 0) + 1,
          percentBps: 0,
        }),
      ];
    });
  };

  const pickShortcut = (value) => {
    if (executionComplete) return;
    setRuleSource("manual");
    // Entrar no "Personalizado" leva a tabela que está na tela, não o padrão à vista.
    if (value === "custom" && shortcut !== "custom")
      setCustomLines(seedLines(validation.profile));
    setShortcut(value);
  };

  const save = async () => {
    if (!canSave) return;
    const scope = saveScope.current;
    setSaving(true);
    setSaveError(null);
    try {
      const trimmedDescription = description.trim();
      const payload = {
        target: ruleTargetOf(chosenTarget),
        profile: validation.profile,
        // Sempre manda `description`, mesmo vazia: é o único jeito de LIMPAR uma
        // descrição já gravada. O cliente da API já descarta apenas `undefined`,
        // então uma string vazia aqui grava vazio de propósito (não é omissão).
        description: trimmedDescription,
        // O carimbo das entradas em que a prévia foi calculada: se a obra mudou desde então
        // o servidor recusa (409) e a prévia é recalculada.
        ...(impact.answer?.previewStamp ? { previewStamp: impact.answer.previewStamp } : {}),
      };
      // Chamador que grava por conta (contrato da Task 16) passa `onSave`; sem ele o
      // editor fala com a rota. O envelope é conferido nos DOIS caminhos: delegar não
      // é motivo para fechar o editor sobre uma gravação que não aconteceu.
      let result;
      if (aiDraft) {
        if (
          aiDraft.workId !== workId ||
          chosenTarget.key !== aiDraft.targetKey
        ) {
          setSaveError(
            "O alvo mudou. Gere outra proposta ou descarte esta para editar manualmente.",
          );
          return;
        }
        const body = {
          expectedVersion: aiDraft.version,
          targetKey: chosenTarget.key,
          profile: validation.profile,
          ...(descriptionDirty ? { description: trimmedDescription } : {}),
        };
        const fingerprint = JSON.stringify([workId, aiDraft.draftId, body]);
        if (confirmationRequest.current?.fingerprint !== fingerprint) {
          confirmationRequest.current = {
            fingerprint,
            idempotencyKey: newIdempotencyKey(),
          };
        }
        if (confirmedResult.current) {
          if (confirmedResult.current.fingerprint !== fingerprint)
            throw new Error(
              "A regra já foi salva. Feche e abra uma nova edição para alterar o conteúdo.",
            );
          result = confirmedResult.current.result;
        } else {
          const receipt = await confirmRuleWithAi(
            workId,
            aiDraft.draftId,
            body,
            { idempotencyKey: confirmationRequest.current.idempotencyKey },
          );
          if (saveScope.current !== scope) return;
          if (
            receipt?.status !== "executed" ||
            receipt.draftId !== aiDraft.draftId ||
            receipt.targetKey !== body.targetKey ||
            receipt.version !== aiDraft.version + 1 ||
            typeof receipt.ruleId !== "string" ||
            !receipt.ruleId ||
            !Number.isSafeInteger(receipt.rulesRevision) ||
            receipt.rulesRevision < 0
          ) {
            throw new Error("invalid_execution_receipt");
          }
          result = {
            ...receipt,
            code: receipt.regeneration?.status === "ready" ? 200 : 202,
          };
          confirmedResult.current = { fingerprint, result };
        }
      } else {
        result = onSave
          ? await onSave(payload)
          : await saveDisbursementRule(workId, {
              ...payload,
              source: ruleSource,
            });
      }
      if (saveScope.current !== scope) return;
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setSaveError(saveErrorText(result));
        if (result?.errorCode === "impact_preview_stale") impact.refresh();
        return;
      }
      await onSaved?.(result);
      if (saveScope.current === scope) onClose?.();
    } catch (error) {
      // O `onSave` do chamador pode rejeitar (rede, 4xx/5xx): virar mensagem na tela
      // em vez de rejeição não tratada, e o botão volta a aceitar clique.
      if (saveScope.current === scope)
        setSaveError(
          aiDraft && error?.message !== "invalid_execution_receipt"
            ? error?.message ?? SAVE_ERROR_TEXT
            : SAVE_ERROR_TEXT,
        );
    } finally {
      if (saveScope.current === scope) setSaving(false);
    }
  };

  return (
    <div className="offcanvas active">
      <div
        className="offcanvas-container large ampli-rule-editor"
        role="dialog"
        aria-modal="true"
        aria-label={titleText}
        tabIndex={-1}
        ref={panelRef}
      >
        <hgroup className="offcanvas-header">
          <div>
            <h2>{titleText}</h2>
            <p className="card-label">
              A regra vale para o alvo e desce para tudo abaixo dele, até que um
              alvo mais específico a sobrescreva.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <i
              className="icon icon-width-24 icon-times background-accent"
              aria-hidden={true}
            ></i>
          </button>
        </hgroup>

        <div className="offcanvas-content">
          {!chosenTarget ? (
            <RuleTargetPicker
              targetType={targetType}
              nodeLevel={nodeLevel}
              search={search}
              candidates={candidates}
              loading={loadingTargets}
              error={targetsError}
              onChangeTargetType={setTargetType}
              onChangeNodeLevel={setNodeLevel}
              onChangeSearch={setSearch}
              onChoose={chooseTarget}
            />
          ) : (
            <section aria-label="Forma de desembolso">
              <h3>Alvo</h3>
              <div className="ampli-rule-editor__chosen">
                <strong className="ampli-rule-editor__target-label">
                  {chosenTarget.label}
                </strong>
                {chosenTarget.path ? (
                  <span className="ampli-rule-editor__target-path">
                    {chosenTarget.path}
                  </span>
                ) : null}
                <TargetValue
                  cents={
                    chosenTarget.plannedCents ??
                    (hydrated?.workId === workId &&
                    hydrated?.key === chosenTarget.key
                      ? hydrated.plannedCents
                      : null)
                  }
                />
                {originName ? (
                  <span className="ampli-rule-editor__origin" data-testid="rule-origin">
                    {`Origem vigente: ${originName}`}
                  </span>
                ) : null}
                {chosenTarget.inheritedFrom ? (
                  <span className="ampli-rule-editor__inherited">
                    {`herdado de ${
                      typeof chosenTarget.inheritedFrom === "string"
                        ? chosenTarget.inheritedFrom
                        : chosenTarget.inheritedFrom.label ??
                          chosenTarget.inheritedFrom.key
                    }`}
                  </span>
                ) : null}
                {!seededTarget && !executionComplete ? (
                  <button
                    type="button"
                    className="button button-outline small"
                    onClick={() => chooseTarget(null)}
                  >
                    Trocar alvo
                  </button>
                ) : null}
                {aiDraft && !executionComplete ? (
                  <button
                    type="button"
                    className="button button-ghost small"
                    disabled={saving}
                    onClick={() => {
                      setAiDraft(null);
                      setRuleSource("manual");
                      setSaveError(null);
                      confirmationRequest.current = null;
                      confirmedResult.current = null;
                    }}
                  >
                    Descartar proposta e editar manualmente
                  </button>
                ) : null}
              </div>

              {overwriteWarning ? (
                <p className="alert alert-info" role="status">
                  Este alvo já tem regra: salvar aqui substitui a forma atual.
                </p>
              ) : null}

              <h3>Como esse alvo é pago?</h3>

              {!isReadOnly && !executionComplete ? (
                <RuleShortcutFields
                  shortcut={shortcut}
                  form={form}
                  errorId={validation.message ? PROFILE_ERROR_ID : undefined}
                  onPick={pickShortcut}
                  onPatch={(patch) =>
                    setForm((current) => ({ ...current, ...patch }))
                  }
                />
              ) : null}

              <RuleProfileTable
                lines={visible.lines}
                editable={
                  !isReadOnly && !executionComplete && shortcut === "custom"
                }
                message={validation.message}
                summary={validation.summary}
                onChangeLine={updateCustomLine}
                onRemoveLine={(lineId) => {
                  setRuleSource("manual");
                  setCustomLines((lines) =>
                    lines.filter((line) => line.lineId !== lineId),
                  );
                }}
                onAddLine={addCustomLine}
              />

              {needsImpact ? (
                <RuleImpactSection
                  impact={impact}
                  readOnly={isReadOnly}
                  onRetry={impact.refresh}
                />
              ) : null}

              {/* O título mora no gráfico: é ele que sabe se a obra está em R$ ou CUB. */}
              <RulePreviewChart
                plannedSeries={previewSeries}
                profile={validation.profile ?? visible.lines}
                hydration={previewHydration}
                originName={originName}
              />

              {!isReadOnly ? (
                <>
                  <h3>Descrição e IA</h3>
                  <label className="ampli-rule-editor__field">
                    <span className="card-label">Descrição (opcional)</span>
                    <input
                      type="text"
                      aria-label="Descrição"
                      value={description}
                      maxLength={500}
                      disabled={executionComplete}
                      onChange={(event) => {
                        if (executionComplete) return;
                        setDescription(event.target.value);
                        setDescriptionDirty(true);
                      }}
                      placeholder="Contrato padrão de elevadores"
                    />
                  </label>

                  {/* Espec. 10.3: texto livre vira PROPOSTA de alvo + perfil; nada é
                      gravado aqui — só o `Salvar` acima grava. */}
                  {!saving && !executionComplete ? (
                    <DescribeRuleWithAi
                      workId={workId}
                      targetKey={chosenTarget.key}
                      onFill={applyAiProposal}
                    />
                  ) : null}
                </>
              ) : null}

              {saveError ? (
                <p className="alert alert-error" role="alert">
                  {saveError}
                </p>
              ) : null}
              {executionComplete ? (
                <p role="status">
                  A regra já foi salva. Atualize a tela para concluir; para
                  alterar o conteúdo, feche e abra uma nova edição.
                </p>
              ) : null}

            </section>
          )}
        </div>
        {chosenTarget ? (
          <footer className="ampli-rule-editor__footer">
            {missing ? (
              <p className="card-label ampli-rule-editor__missing" role="status">
                {missing}
              </p>
            ) : null}
            <button
              type="button"
              className="button button-ghost small"
              onClick={onClose}
            >
              {isReadOnly ? "Fechar" : "Cancelar"}
            </button>
            {!isReadOnly ? (
              <button
                type="button"
                className="button button-accent small"
                disabled={!canSave}
                onClick={save}
              >
                {executionComplete
                  ? "Atualizar tela"
                  : impactedCount > 0
                    ? "Reprogramar desembolso"
                    : "Salvar regra"}
              </button>
            ) : null}
          </footer>
        ) : null}
      </div>
      <div onClick={onClose} className="offcanvas-background" />
    </div>
  );
};

export default RuleEditorOffcanvas;
